// Dialog: ghi nợ tay cho khách (nợ cũ đầu kỳ / nợ ngoài đơn) → POST /v1/debts/owe
// Manager+ (BE cũng chặn). Cộng thẳng vào số dư công nợ, có bút toán để truy vết.
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { X } from "lucide-react";

interface Props {
  open: boolean;
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddDebtDialog({ open, customerId, customerName, onClose, onSuccess }: Props) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("Nợ cũ đầu kỳ");
  const [error, setError] = useState("");

  const oweMut = useMutation({
    mutationFn: () =>
      apiPost("/v1/debts/owe", {
        customerId,
        amount: parseFloat(amount),
        note,
      }),
    onSuccess: () => {
      setAmount(""); setNote("Nợ cũ đầu kỳ"); setError("");
      onSuccess();
    },
    onError: (err: unknown) => setError(translateApiError(err)),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError("Số tiền phải lớn hơn 0"); return; }
    oweMut.mutate();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Ghi nợ đầu kỳ — {customerName}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={oweMut.isPending}>
            <X size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

          <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Dùng để nhập số nợ CŨ của khách từ trước khi lên hệ thống (hoặc khoản nợ ngoài
            đơn hàng). Nợ từ đơn bán trả thiếu được ghi tự động — không nhập lại ở đây.
          </p>

          <div className="space-y-1">
            <label className="text-sm font-medium">Số tiền nợ (₫) *</label>
            <MoneyInput value={amount} onChange={setAmount} placeholder="0" autoFocus />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Ghi chú</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nợ cũ đầu kỳ"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose} disabled={oweMut.isPending} className="flex-1">
              Hủy
            </Button>
            <Button type="submit" disabled={oweMut.isPending} className="flex-1">
              {oweMut.isPending ? "Đang xử lý..." : "Ghi nợ"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
